import React, { useState, useEffect, useCallback, memo } from "react";
import type { BiddingInfoItem, MasterDataForComponents } from "../../../types";
import {
  CaltivationCategoryOptions,
  ListingCategoryConst,
  ProductionHistoryOptions,
} from "../../../constants";
import { formatToDateInput, formatFromDateInput } from "../../../utils/date";
import { NumericFormat } from "react-number-format";
import { TEA_CATEGORY } from "../../../constants/TeaCategoryConst";
import { Search } from 'lucide-react';
import { MasterSearchDialog } from '../../common/MasterSearch/MasterSearchDialog';
import { useCodeName } from '../../../hooks/useUserMasterCodeName';
import { MasterCategoryConst, type MasterCategoryType } from '../../../types/masterSearchTypes';

//----------------
// Props
//----------------
interface BiddingItemMaintenanceEditFormProps {
  originalItem: BiddingInfoItem;
  onSave: (updatedItem: BiddingInfoItem) => void;
  onCancel: () => void;
  isCreating?: boolean;
  isSaving?: boolean;
  masterData: MasterDataForComponents;
  isSeller: boolean;
  isFarmer: boolean;
}

//--------------------------------
// 出品管理の編集コンポーネント
//--------------------------------
// 「その他」の品種コードを定義
const OTHER_VARIETY_CODE = 47;
export const BiddingItemMaintenanceEditForm = memo(
  ({
    originalItem,
    onSave,
    onCancel,
    isCreating = false,
    isSaving = false,
    masterData,
    isSeller,
    isFarmer,
  }: BiddingItemMaintenanceEditFormProps) => {
    //----------------
    // State定義
    //----------------
    const [formData, setFormData] = useState<BiddingInfoItem>({
      ...originalItem,
    });

    // カスタムフック：名称表示用の codes/names 管理
    const { codes, setCodes, names } = useCodeName();

    // 検索ダイアログ制御用の状態
    const [isSearchOpen, setIsSearchOpen] = useState(false); // ダイアログの開閉
    const [searchCategory, setSearchCategory] = useState<MasterCategoryType>(MasterCategoryConst.SELLER); // 検索対象マスタ
    const [activeTarget, setActiveTarget] = useState<'seller' | 'farmer'>('seller'); // どの入力欄に対する検索か

    //----------------
    // Memo定義
    //----------------

    //----------------
    // Effect定義
    //----------------
    useEffect(() => {
    if (isCreating) {
      const isCopy = !!originalItem.sellerCode;

      if (isCopy) {
        // コピーモード
        setFormData({
          ...originalItem,
          gapCode: originalItem.gapCode ?? 9,
        });
        // 既存コードをフック側に反映
        setCodes(p => ({ ...p, seller: originalItem.sellerCode || null, farmer: originalItem.farmerCode || null }));
      } else {
        // 新規登録モード
        setFormData({
          ...originalItem, // ← ここに親でセットした biddingDate (APIの日付) が入っている
          productionHistory: 1,
          caltivationCategory: 1,
          gapCode: 9,
          teaCategory: 1,
          isFarmerHidden: false,
        });
      }
    } else {
      // 編集モード
      setFormData({ ...originalItem });
      // 既存コードをフック側に反映
      setCodes(p => ({ ...p, seller: originalItem.sellerCode || null, farmer: originalItem.farmerCode || null }));
    }
  }, [originalItem, isCreating, setCodes]);

    // [品種]セレクトボックスで値選択された際の[品種名]に対する挙動
    useEffect(() => {
    if (formData.varietyCode === OTHER_VARIETY_CODE) 
      {
        const isMasterName = masterData.varieties.some(
          (v) => v.name === formData.varietyName && v.id !== String(OTHER_VARIETY_CODE)
        );

        // マスタの名前が入っている（＝直前まで特定の品種を選んでいた）ならクリアする
        if (isMasterName) {
        setFormData((prev) => ({ ...prev, varietyName: "" }));
      }
      } else if (formData.varietyCode) {
        // 「その他」以外が選ばれたら、マスターから名前を検索してセット
        const name =
          masterData.varieties.find(
            (v) => v.id === String(formData.varietyCode),
          )?.name || "";
        // formData.varietyName が既に正しい名前でなければセット
        if (name !== "N/A" && formData.varietyName !== name) {
          setFormData((prev) => ({ ...prev, varietyName: name }));
        }
      } else {
        // 「選択...」が選ばれたら（varietyCodeが''）、名称をクリア
        if (formData.varietyName !== "") {
          setFormData((prev) => ({ ...prev, varietyName: "" }));
        }
      }
    }, [formData.varietyCode, masterData.varieties]);

    //----------------
    // イベントハンドラー定義
    //----------------

    // 入力値変更時にフォームに同期させる
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (name === "brand") {
          let byteCount = 0;
          for (let i = 0; i < value.length; i++) {
            const charCode = value.charCodeAt(i);
            // 半角英数字(0x00-0x7F) と 半角カナ(0xFF61-0xFF9F) は 1バイト、それ以外（全角）は 2バイト
            if ((charCode >= 0x00 && charCode <= 0x7f) || (charCode >= 0xff61 && charCode <= 0xff9f)) {
              byteCount += 1;
            } else {
              byteCount += 2;
            }
          }
          if (byteCount > 18) {
            return; // 18バイト（全角なら9文字）を超える場合は入力をブロックする
          }
        }

        const isCheckbox = type === "checkbox";
        const checked = (e.target as HTMLInputElement).checked;

        const isNumber =
          type === "number" ||
          [
            "sellerCode",
            "farmerCode",
            "varietyCode",
            "teaTypeCode",
            "teaSeasonCode",
            "listingCategory",
            "bidMinPrice",
            "gapCode",
            "caltivationCategory",
            "productionHistory",
            "capacity1",
            "quantity1",
            "capacity2",
            "quantity2", // 1と2を追加
            "teaCategory",
          ].includes(name);

        let processedValue: string | number | boolean = value;
        let sanitizedValue = value;
        if (isCheckbox) {
          processedValue = checked;
        } else if (isNumber) {
          sanitizedValue = value.replace(/[^0-9]/g, '');
          processedValue = sanitizedValue === "" ? "" : Number(sanitizedValue);
        } else if (name === "biddingDate") {
          processedValue = formatFromDateInput(value);
        }

        // 複数の状態更新を1つの setFormData にまとめ、連動処理を安全に実行する
        setFormData((prev) => {
          const nextState = { ...prev, [name]: processedValue };

          // 売り手が変わったら生産者をリセット ＋ GAP区分を9にする
          if (name === "sellerCode") {
            const numVal = sanitizedValue === "" ? null : Number(sanitizedValue);
            setCodes(p => ({ ...p, seller: numVal, farmer: null })); // フック側同期
            nextState.farmerCode = "";
            nextState.farmerName = "";
            nextState.gapCode = 9;
          }

          // 品種が「その他」以外に変更されたら、品種名をクリア
          if (name === "varietyCode" && Number(value) !== OTHER_VARIETY_CODE) {
            nextState.varietyName = "";
          }

          if (name === "farmerCode") {
          const numVal = sanitizedValue === "" ? null : Number(sanitizedValue);
          setCodes(p => ({ ...p, farmer: numVal })); // フック側同期
          const code = sanitizedValue === "" ? "" : Number(sanitizedValue);
          nextState.farmerCode = code;

          if (code !== "") {
          // 【マスタから生産者選択】
          const selectedFarmer = masterData.farmers.find(
            (f) =>
              String(f.sellerCode) === String(nextState.sellerCode) &&
              Number(f.farmerCode) === code
          );
          if (selectedFarmer) {
            nextState.farmerName = selectedFarmer.farmerName;
            nextState.gapCode = selectedFarmer.gapCode ?? 9;
          }
          } else {
            // 【手入力モード】（セレクトボックスで「選択」を選んでいる場合（初期表示））
            nextState.farmerName = ""; 
            nextState.gapCode = 9;
          }
        }

          return nextState;
        });
      },
      [masterData.farmers, setCodes], //setFormData内でマスタを参照するため
    );

    /** マスタ検索ダイアログを表示する */
    const openSearch = (cat: MasterCategoryType, target: 'seller' | 'farmer') => {
      // 生産者検索は、親となる売手コードが入力されている場合のみ許可
      if (target === 'farmer' && !formData.sellerCode) return;

      setSearchCategory(cat);
      setActiveTarget(target);
      setIsSearchOpen(true);
    };

    /** ダイアログでマスタを選択した際の処理 */
    const handleSelectMaster = (selectedData: any) => {
      if (activeTarget === 'seller') {
        const code = selectedData.sellerCode ?? null;
        setCodes(p => ({ ...p, seller: code, farmer: null }));
        setFormData(p => ({ 
          ...p, 
          sellerCode: code ?? "", 
          farmerCode: "", 
          farmerName: "", 
          gapCode: 9 
        }));
        setTimeout(() => {
          const nextEl = document.getElementsByName('farmerCode')[0] as HTMLElement;
          nextEl?.focus();
        }, 100);
      } else if (activeTarget === 'farmer') {
        const fCode = selectedData.farmerCode ?? null;
        setCodes(p => ({ ...p, farmer: fCode }));
        setFormData(p => ({ 
          ...p, 
          farmerCode: fCode ?? "", 
          farmerName: selectedData.farmerName || "",
          gapCode: selectedData.gapCode ?? 9
        }));
        setTimeout(() => {
          const nextEl = document.getElementsByName('teaSeasonCode')[0] as HTMLElement;
          nextEl?.focus();
        }, 100);        
      }
      setIsSearchOpen(false);
    };

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter") {
        // 備考欄などでEnterを押したときにフォーム送信されるのを防ぐ
        e.preventDefault();

        const container = e.currentTarget.closest("div.bg-white") || document;
        // フォーカス可能な要素をすべて取得
        const focusableElements = Array.from(
          container.querySelectorAll('input:not([disabled]), select:not([disabled]), button:not([disabled])')
        ) as HTMLElement[];

        let index = focusableElements.indexOf(e.currentTarget);

        if (index > -1) {
          let nextIndex = index + 1;

          while (nextIndex < focusableElements.length) {
            const nextElement = focusableElements[nextIndex] as HTMLInputElement;
            
            const isCheckbox = nextElement.type === 'checkbox';
            const isRadio = nextElement.type === 'radio';
            // 検索ボタン（アイコンボタン）はスキップしたいが、保存ボタン（テキストがあるボタン）は止めたい
            // もしくは「bg-indigo-600」などのクラス名で判定する
            const isSearchButton = nextElement.tagName === 'BUTTON' && !nextElement.innerText.includes("保存");
            const isDisabled = (nextElement as any).disabled;

            if (isCheckbox || isRadio || isSearchButton || isDisabled) {
              nextIndex++;
            } else {
              // 保存ボタン、または次の入力項目が見つかったらフォーカス
              nextElement.focus();
              break;
            }
          }
        }
      }
    }, []);

    //----------------
    // その他の定義
    //----------------

    // [出品区分]の表示内容制御
    const isSplit = originalItem.subItems && originalItem.subItems.length > 0;

    // [品種名]入力制御
    const isVarietyOther = formData.varietyCode === OTHER_VARIETY_CODE;

    // 入力項目のスタイル定義
    const inputClasses =
      "mt-1 w-full p-2 border border-slate-300 rounded-md bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500 h-[42px]"; // 共通スタイル
    const disabledInputClasses = `${inputClasses} disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed`; // disabled時のスタイル

    // マスタコード入力欄専用スタイル（左側のみ角丸、検索ボタンと結合するため）
    const codeInputClasses = "mt-1 w-20 p-2 border border-slate-300 rounded-l-md bg-yellow-50 focus:ring-1 focus:ring-yellow-500 text-sm h-[42px] outline-none shadow-sm disabled:bg-slate-200 disabled:cursor-not-allowed text-left";

    //----------------
    // レンダリング処理
    //----------------
    return (
      <div className="bg-white p-4 md:p-6 rounded-lg border shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center gap-6 mb-6 ">
          {/* タイトル */}
          <h3 className="text-2xl font-bold text-slate-800">
            {isCreating ? "新規出品情報登録" : "出品情報の編集"}
          </h3>

          {/* 荒茶・仕上茶 ラジオボタン */}
          {/* スマホでは幅いっぱい(w-full)にして押しやすくする */}
          <div className="flex items-center justify-center gap-6 bg-slate-50 px-3 py-1 rounded-xl border border-slate-200 w-full md:w-auto mt-1">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="teaCategory"
                value={TEA_CATEGORY.ARACHA} // 荒茶の値を 1 に設定
                checked={Number(formData.teaCategory) === TEA_CATEGORY.ARACHA} // Stateと連動
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                className="w-6 h-6 text-indigo-600 border-slate-300 focus:ring-indigo-500"
              />
              <span className="ml-3 text-lg font-bold text-slate-700">
                荒茶
              </span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="teaCategory" //teaCategory に変更
                value={TEA_CATEGORY.SHIAGECHA} //仕上茶の値を 2 に設定
                checked={
                  Number(formData.teaCategory) === TEA_CATEGORY.SHIAGECHA
                }
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                className="w-6 h-6 text-indigo-600 border-slate-300 focus:ring-indigo-500"
              />
              <span className="ml-3 text-lg font-bold text-slate-700">
                仕上茶
              </span>
            </label>
          </div>
        </div>

        {/* --- １段目：入札日のみ --- */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-x-2 gap-y-2 mb-2">
          <div className="form-group col-span-2 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              入札日
            </label>
            <input
              type="date"
              name="biddingDate"
              value={formatToDateInput(formData.biddingDate)}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={`${inputClasses} appearance-none min-w-0 safari-date-fix`}
            />
          </div>
        </div>

        {/* --- ２段目：売手、生産者 --- */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-x-2 gap-y-2 mb-2">
          {/* 売手 */}
          <div className="form-group col-span-2 md:col-span-3">
            <label className="block text-sm font-medium text-slate-600">
              売手
            </label>
            <div className="flex items-center gap-0">
              <input
                type="text"
                name="sellerCode"
                value={codes.seller ?? ''}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                disabled={isSeller || isFarmer}
                className={codeInputClasses}
              />
              <button 
                type="button"
                disabled={isSeller || isFarmer}
                onClick={() => openSearch(MasterCategoryConst.SELLER, 'seller')} 
                className="mt-1 px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[42px] flex items-center justify-center transition-colors disabled:bg-slate-200"
              >
                <Search size={16} className="text-slate-500" />
              </button>
              {/* 名称表示：ml-1 でボタンとの距離を詰め、flex-1 で残りの幅を埋める */}
              <div className="ml-1 mt-1 flex-1 text-sm text-slate-500 truncate font-medium bg-slate-200 border border-slate-300 rounded-md px-3 h-[42px] flex items-center">
                {names.seller}
              </div>
            </div>
          </div>

          {/* 生産者 */}
          <div className="form-group col-span-2 md:col-span-3">
            <div className="flex items-center mb-1 h-6">
              {/* 左側：タイトルラベル */}
              <label className="text-sm font-medium text-slate-600 whitespace-nowrap">
                生産者
              </label>

              {/* 非表示チェックボックス */}
              <div className="ml-auto">
                  <label className="flex items-center gap-1 cursor-pointer select-none whitespace-nowrap">
                    <span className="text-[11px] text-slate-500">非表示</span>
                    <input
                      type="checkbox"
                      name="isFarmerHidden"
                      checked={!!formData.isFarmerHidden}
                      onKeyDown={handleKeyDown}
                      onChange={handleChange}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                  </label>
                </div>
              </div>
            
            <div className="flex items-center gap-0">
              <input
                type="text"
                name="farmerCode"
                value={codes.farmer ?? ''}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                disabled={isFarmer || !formData.sellerCode}
                className={codeInputClasses}
              />
              <button 
                type="button"
                disabled={isFarmer || !formData.sellerCode}
                onClick={() => openSearch(MasterCategoryConst.FARMER, 'farmer')} 
                className="mt-1 px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md hover:bg-slate-200 h-[42px] flex items-center justify-center transition-colors disabled:bg-slate-200"
              >
                <Search size={16} className="text-slate-500" />
              </button>
              {/* 名称入力/表示：ml-1 でボタンとの距離を詰める */}
              <div className="ml-1 mt-1 flex-1">
                <input
                  type="text"
                  name="farmerName"
                  value={formData.farmerCode ? names.farmer : (formData.farmerName || "")}
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  disabled={isFarmer || !!formData.farmerCode} 
                  placeholder={formData.farmerCode ? "" : "生産者名を手入力"}
                  className={`w-full text-sm font-medium px-3 border rounded-md h-[42px] outline-none transition-colors
                    ${!!formData.farmerCode 
                      ? "bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed" 
                      : "bg-yellow-50 text-slate-800 border-slate-300 focus:ring-yellow-500 focus:border-yellow-500 shadow-sm"
                    }`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* スマホでは2列、中画面以上で6列 */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-x-2 gap-y-2">
          {/* --- ３行目：茶期、茶種、荷印 (各 1/3 幅) --- */}
          <div className="form-group col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              茶期
            </label>
            <select
              name="teaSeasonCode"
              value={formData.teaSeasonCode}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">選択</option>
              {masterData.teaSeasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              茶種
            </label>
            <select
              name="teaTypeCode"
              value={formData.teaTypeCode}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">選択</option>
              {masterData.teaTypes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              荷印
            </label>
            <input
              type="text"
              name="brand"
              value={formData.brand}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>

          {/* --- ４行目：品種、品種名、出品区分 (各 1/3 幅) --- */}
          <div className="form-group col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              品種名
            </label>
            <select
              name="varietyCode"
              value={formData.varietyCode}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            >
              <option value="">選択</option>
              {masterData.varieties.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              その他品種名
            </label>
            <input
              type="text"
              name="varietyName"
              value={formData.varietyName || ""}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={disabledInputClasses}
              disabled={!isVarietyOther}
              placeholder={isVarietyOther ? "品種名を入力" : "品種名を選択"}
            />
          </div>

          {/* --- GAP区分、有機、生産履歴 --- */}
          <div className="form-group col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              GAP区分
            </label>
            <select
              name="gapCode"
              // 値がない場合は 9 (GAPなし) とする
              value={formData.gapCode ?? 9}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            >
              {/* 0(未選択) を廃止し、9(GAPなし) をデフォルトの選択肢にする */}
              <option value={9}>GAPなし</option>

              {/* マスタデータから 9 と 0 を省いて表示 */}
              {masterData.gaps
                .filter((g) => g.gapCode !== 9 && g.gapCode !== 0)
                .map((g) => (
                  <option key={g.gapCode} value={g.gapCode}>
                    {g.gapName}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group col-span-2 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              出品区分
            </label>
            <div className="mt-2 flex gap-4">               
              {/* --- 通常入札 --- 分割済みの時だけ無効 */}
              <label
                className={`flex items-center ${isSplit ? "cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  name="listingCategory"
                  value={ListingCategoryConst.BIDDING}
                  checked={
                    formData.listingCategory === ListingCategoryConst.BIDDING
                  }
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  disabled={isSplit}
                />
                <span className={`ml-2 ${isSplit ? "text-slate-400" : ""}`}>
                  通常入札
                </span>
              </label>

              {/* --- 相対 --- (常に無効化) */}
              <label className="flex items-center cursor-not-allowed">
                <input
                  type="radio"
                  name="listingCategory"
                  value={ListingCategoryConst.PRIVATE}
                  checked={
                    formData.listingCategory === ListingCategoryConst.PRIVATE
                  }
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  disabled={true} // 常に選択不可にする
                />
                <span className="ml-2 text-slate-400">
                  相対
                </span>
              </label>

              {/* --- 入札会 --- (常に無効化) */}
              <label className="flex items-center cursor-not-allowed">
                <input
                  type="radio"
                  name="listingCategory"
                  value={ListingCategoryConst.SALE}
                  checked={
                    formData.listingCategory === ListingCategoryConst.SALE
                  }
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  disabled={true} // 常に選択不可にする
                />
                <span className="ml-2 text-slate-400">
                  入札会
                </span>
              </label>

            </div>
          </div>

          <div className="form-group col-span-2 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              栽培区分
            </label>
            <div className="mt-2 flex gap-4">
              {CaltivationCategoryOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center ${isSplit ? "cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name="caltivationCategory"
                    value={option.value}
                    checked={formData.caltivationCategory === option.value}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isSplit}
                  />
                  <span className={`ml-2 ${isSplit ? "text-slate-400" : ""}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group col-span-2 md:col-span-2">
            <label className="block text-sm font-medium text-slate-600">
              生産履歴
            </label>
            <div className="mt-2 flex gap-4">
              {ProductionHistoryOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center ${isSplit ? "cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name="productionHistory"
                    value={option.value}
                    checked={formData.productionHistory === option.value}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    disabled={isSplit}
                  />
                  <span className={`ml-2 ${isSplit ? "text-slate-400" : ""}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* --- ５行目：①容量/本数、②容量/本数 (各 1/2 幅) --- */}
          {/* ① 容量 (kg/本) × 本数 */}
          <div className="col-span-2 md:col-span-3">
            <label className="block text-sm font-medium text-slate-600">
              ① 容量 (kg) × 本数 (本)
            </label>
            <div className="flex items-center gap-2">
              <NumericFormat
                name="capacity1"
                value={formData.capacity1 || ""}
                placeholder="容量"
                disabled={isSplit}
                className={`${disabledInputClasses}`}
                allowNegative={false}
                decimalScale={1}
                onKeyDown={handleKeyDown}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    capacity1: floatValue ?? "",
                  }));
                }}
              />
              <span className="text-slate-500 translate-y-0.5">×</span>
              <NumericFormat
                name="quantity1"
                value={formData.quantity1 || ""}
                placeholder="本数"
                disabled={isSplit}
                className={`${disabledInputClasses}`}
                allowNegative={false}
                decimalScale={0}
                onKeyDown={handleKeyDown}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    quantity1: floatValue ?? "",
                  }));
                }}
              />
            </div>
            {isSplit && (
              <p className="text-xs text-slate-500 mt-1">
                分割済のため変更不可
              </p>
            )}
          </div>

          {/* ② 容量 (kg/本) × 本数 */}
          <div className="col-span-2 md:col-span-3">
            <label className="block text-sm font-medium text-slate-600">
              ② 容量 (kg) × 本数 (本)
            </label>
            <div className="flex items-center gap-2">
              <NumericFormat
                name="capacity2"
                value={formData.capacity2 || ""}
                placeholder="容量"
                disabled={isSplit}
                className={`${disabledInputClasses}`}
                allowNegative={false}
                decimalScale={1}
                onKeyDown={handleKeyDown}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    capacity2: floatValue ?? "",
                  }));
                }}
              />
              <span className="text-slate-500 translate-y-0.5">×</span>
              <NumericFormat
                name="quantity2"
                value={formData.quantity2 || ""}
                placeholder="本数"
                disabled={isSplit}
                className={`${disabledInputClasses}`}
                allowNegative={false}
                decimalScale={0}
                onKeyDown={handleKeyDown}
                onValueChange={(values) => {
                  const { floatValue } = values;
                  setFormData((prev) => ({
                    ...prev,
                    quantity2: floatValue ?? "",
                  }));
                }}
              />
            </div>
            {isSplit && (
              <p className="text-xs text-slate-500 mt-1">
                分割済のため変更不可
              </p>
            )}
          </div>

          {/* --- ６行目：最低落札値(円) (各 1/2 幅) --- */}
          <div className="form-group col-span-2 md:col-span-2 -mb-2">
            <label className="block text-sm font-medium text-slate-600 mt-2">
              最低落札値(円)
            </label>
            <NumericFormat
              value={formData.bidMinPrice === 0 ? "" : formData.bidMinPrice}
              thousandSeparator={true}
              allowNegative={false}
              decimalScale={0}
              placeholder="最低落札値"
              // disabled={isFarmer}
              // 値が入っていれば右寄せ(text-right)、空なら左寄せ(text-left)
              className={`${inputClasses} ${formData.bidMinPrice ? "text-right" : "text-left"}`}
              isAllowed={(values) => {
                const { value } = values;
                return value.length <= 7; 
              }}
              onKeyDown={handleKeyDown}
              onValueChange={(values) => {
                setFormData((prev) => ({
                  ...prev,
                  bidMinPrice:
                    values.floatValue === undefined ? "" : values.floatValue,
                }));
              }}
            />
          </div>

          {/* --- X行目：備考 --- */}
          <div className="form-group col-span-2 md:col-span-4">
            <label className="block text-sm font-medium text-slate-600 mt-2">
              備考
            </label>
            <input
              type="text"
              name="biko"
              value={formData.biko || ""}
              placeholder="備考"
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>
        </div>
        {/* --- 6列グリッドの終了 --- */}
        {/* --- ボタンエリア (変更なし) --- */}
        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={onCancel}
            className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-slate-200 hover:bg-slate-300 active:bg-slate-400 active:scale-95 transition-all duration-150 font-medium"
          >
            キャンセル
          </button>
          <button
            onClick={() => onSave(formData)}
            className="w-full md:w-auto px-4 py-3 md:py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 active:scale-95 transition-all duration-150 font-semibold shadow-sm"
            disabled={isSaving}
          >
            {isSaving ? "登録中..." : "変更を保存"}
          </button>
        </div>

        {/* 汎用マスタ検索ダイアログ */}
        <MasterSearchDialog 
          isOpen={isSearchOpen} 
          onClose={() => setIsSearchOpen(false)} 
          category={searchCategory} 
          initialValues={{ sellerCode: formData.sellerCode, sellerName: names.seller }} 
          onSelect={handleSelectMaster} 
        />
      </div>
    );
  },
);